import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { ViewModeItem } from "../models/ViewModeItem";
import { ViewModeItems } from "../models/ViewModeItem";
import { SortItem, AvailableSortItems } from "../models/SortItem";
import { LookupItem } from "../models/LookupItem";
import { FacetResult } from "../models/FacetResult";
import { FinLitItem } from "../models/FinLitItem";
import { SuggestItem } from '../models/SuggestItem';
import { SuggestFilter } from '../models/common/SuggestFilter';

export interface FinLitState {
  viewMode: ViewModeItem;
  orderBy: SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  selectedSessionLevels: undefined | LookupItem;
  selectedAudience: undefined | LookupItem;
  isLoading: boolean;
  canLoadMore: boolean;
  isSuggestLoading: boolean;
  items: FinLitItem[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: FinLitState = {
  viewMode: ViewModeItems[0],
  orderBy: AvailableSortItems[1],
  keywords: "",
  suggestions: [],
  selectedSessionLevels: undefined,
  selectedAudience: undefined,
  isLoading: false,
  canLoadMore: false,
  isSuggestLoading: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  sessionLevel?: string;
    audience?: string;
    path?: string;
    page: number;
    pageSize: number;
//  sortBy?: string;
//  sortAsc?: boolean;
//  path?: string;
//  page: number;
//  pageSize: number;
//  filterOnlyEventDetailsType: boolean;
//  includedIds?: string[],
//  excludedIds?: string[],
};

export const searchFinLitSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/searchFinLitSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/finlitsessionsearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);

export const searchFinLit = createAsyncThunk<
  FacetResult<FinLitItem>,
  SearchFilter
>(
  "data/searchFinLit",
    async (payload: SearchFilter): Promise<FacetResult<FinLitItem>> => {
      const response = await fetch("/finlitsessionsearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
        return (await response.json()) as FacetResult<FinLitItem>;
  }
);

export const finLitSlice = createSlice({
  name: "finLit",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords!== action.payload) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.keywords = action.payload;
      state.page = 1;
    },
    changeSessionLevel: (state, action: PayloadAction<undefined | LookupItem>) => {
      if (state.selectedSessionLevels?.value !== action.payload?.value) {
        state.items = [];
        state.canLoadMore = false;
      }
        state.selectedSessionLevels = action.payload;
      state.page = 1;
    },
    changeAudience: (state, action: PayloadAction<undefined | LookupItem>) => {
      if (state.selectedAudience?.value !== action.payload?.value) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.selectedAudience = action.payload;
      state.page = 1;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
        .addCase(searchFinLit.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
          searchFinLit.fulfilled,
          (state, action: PayloadAction<FacetResult<FinLitItem>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
        .addCase(searchFinLit.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchFinLitSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchFinLitSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchFinLitSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changePage,
  changeViewMode,
  changeKeywords,
  changeSessionLevel,
  changeAudience,
  changeInit,
  clearSuggestions
} = finLitSlice.actions;

export default finLitSlice.reducer;
